import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Pencil, KeyRound } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ui } from "../../../components/ui";
import { Button } from "../../../components/ui/button";
import { LeaveActionToast } from "../../leave-management/components/LeaveActionToast";
import {
  getTenants,
  getExpiringTenants,
  getTenantsByActiveStatus,
  searchTenantsByName,
  toggleTenantStatus,
  getTenantCount,
  getTenantCountByActiveStatus } from
"../../../services/tenantService";
import { getServiceCatalog, getServiceCatalogTree, getTenantsByService, getTenantServiceGrants } from "../../../services/tenantServicesService";
import { StatusToggleWithPill } from "../components/StatusPill";
import AccessTree from "../components/AccessTree";

const STATUS_TABS = [
{ key: "all", label: "All" },
{ key: "active", label: "Active" },
{ key: "expiring", label: "Expiring soon" },
{ key: "expired", label: "Expired" }];


const TABS = [
{ key: "registry", label: "Tenant Registry" },
{ key: "catalog", label: "Services & Modules Catalog" }];


function formatDate(raw) {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const initials = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || "");
  return initials.join("") || "?";
}

function getAccessSummary(tenant) {
  const grants = tenant?.serviceAccess || tenant?.accessSummary || tenant?.moduleAccess || null;
  if (!grants || !Array.isArray(grants) || grants.length === 0) return null;

  return grants
  .map((g) => g.serviceName || g.name)
  .filter(Boolean)
  .join(", ");
}

function TenantsMobileList({ tenants, onEdit, onManageAccess, onToggleStatus }) {
  return (
    <ul className="divide-y divide-[var(--color-text-border)]/35 md:hidden">
      {tenants.map((tenant, idx) => {
        const rowKey = tenant.tenantId || idx;
        return (
          <li key={rowKey} className="px-4 py-3.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-[var(--color-text-secondary)] truncate">
                  <Trans i18nKey="common.id2" /> {tenant.tenantId}
                </p>
                <p className="text-sm font-semibold text-[var(--color-primary)] truncate">
                  {tenant.tenantName || "Untitled tenant"}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <StatusToggleWithPill checked={!!tenant.isActive} onChange={() => onToggleStatus(tenant)} />
                </div>
                <p className="mt-1.5 text-xs text-[var(--color-text-secondary)]">
                  {formatDate(tenant.subscriptionStartDate)} – {formatDate(tenant.subscriptionEndDate)}
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => onManageAccess(tenant)}
                  className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/10 px-2.5 py-1.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/15 transition">
                  
                  <KeyRound className="h-3.5 w-3.5" />
                  <Trans i18nKey="tenantManagement.access" />
                </button>
                <button
                  type="button"
                  onClick={() => onEdit(tenant.tenantId)}
                  className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/10 px-2.5 py-1.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/15 transition">
                  
                  <Pencil className="h-3.5 w-3.5" />
                  <Trans i18nKey="common.edit" />
                </button>
              </div>
            </div>
          </li>);

      })}
    </ul>);

}

function TenantsTable({ tenants, onEdit, onManageAccess, onToggleStatus }) {
  const [failedLogos, setFailedLogos] = useState({});

  if (tenants.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-sm text-[var(--color-text-secondary)]"><Trans i18nKey="tenantManagement.noTenantsMatchYourSearch" /></p>
      </div>);

  }

  return (
    <>
      <TenantsMobileList tenants={tenants} onEdit={onEdit} onManageAccess={onManageAccess} onToggleStatus={onToggleStatus} />
      <div className="hidden md:block w-full overflow-x-auto">
        <table className="w-full text-sm min-w-[940px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30">
              <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="common.id" /></th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="tenantManagement.tenant" /></th>
              <th className="px-4 py-3 font-semibold min-w-[200px]"><Trans i18nKey="tenantManagement.access" /></th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="tenantManagement.membership" /></th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="status.status" /></th>
              <th className="px-4 py-3 font-semibold text-center whitespace-nowrap"><Trans i18nKey="common.actions" /></th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant, idx) => {
              const rowKey = tenant.tenantId || idx;
              const accessSummary = getAccessSummary(tenant);
              return (
                <tr
                  key={rowKey}
                  className="hover:bg-[var(--color-workspace)]/20 transition-colors border-b border-[var(--color-text-border)]/35 last:border-b-0">
                  
                  <td className="px-4 py-3 text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
                    {tenant.tenantId}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {tenant.tenantLogo && !failedLogos[rowKey] ?
                      <img
                        src={tenant.tenantLogo}
                        alt=""
                        className="h-8 w-8 rounded-lg object-cover shrink-0"
                        onError={() => setFailedLogos((prev) => ({ ...prev, [rowKey]: true }))} /> :


                      <div className="h-8 w-8 rounded-lg bg-[var(--color-workspace)]/60 text-[var(--color-text-secondary)] flex items-center justify-center text-xs font-bold shrink-0">
                          {getInitials(tenant.tenantName)}
                        </div>
                      }
                      <span className="font-semibold text-[var(--color-primary)] truncate">
                        {tenant.tenantName || "Untitled tenant"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {accessSummary ?
                    <span className="text-[13px] text-[var(--color-text-secondary)]">{accessSummary}</span> :

                    <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--color-warning)]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-warning)] shrink-0" />
                        <Trans i18nKey="tenantManagement.accessNotConfigured" />
                      </span>
                    }
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
                    {formatDate(tenant.subscriptionStartDate)} – {formatDate(tenant.subscriptionEndDate)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusToggleWithPill checked={!!tenant.isActive} onChange={() => onToggleStatus(tenant)} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => onManageAccess(tenant)}
                        title={i18n.t("tenantManagement.manageAccess")}
                        className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/10 px-2.5 py-1.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/15 transition">
                        
                        <KeyRound className="h-3.5 w-3.5" />
                        <Trans i18nKey="tenantManagement.access" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onEdit(tenant.tenantId)}
                        title={i18n.t("formActions.editTenant")}
                        className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/10 px-2.5 py-1.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/15 transition">
                        
                        <Pencil className="h-3.5 w-3.5" />
                        <Trans i18nKey="common.edit" />
                      </button>
                    </div>
                  </td>
                </tr>);

            })}
          </tbody>
        </table>
      </div>
    </>);

}

function TenantRegistryTab({ onEditTenant, onManageAccess }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [toast, setToast] = useState(null);

  // Counts for the "All / Active / Expiring soon / Expired" tab badges.
  // Pulled independently from the count endpoints (not derived from
  // whatever list happens to be loaded), so they stay correct regardless
  // of which tab/filter is currently active.
  const [tenantCounts, setTenantCounts] = useState({
    all: null,
    active: null,
    expired: null,
    expiring: null
  });

  const loadTenantCounts = async () => {
    const parseCount = (c) => typeof c === "number" ? c : c?.count ?? c?.total ?? null;
    const today = new Date().toISOString().slice(0, 10);

    const [totalRes, activeRes, inactiveRes, expiringRes] = await Promise.allSettled([
    getTenantCount(),
    getTenantCountByActiveStatus(true),
    getTenantCountByActiveStatus(false),
    getExpiringTenants(30)]
    );

    [
    ["getTenantCount", totalRes],
    ["getTenantCountByActiveStatus(true)", activeRes],
    ["getTenantCountByActiveStatus(false)", inactiveRes],
    ["getExpiringTenants", expiringRes]]
    .forEach(([label, res]) => {
      if (res.status === "rejected") {
        console.error(`[TenantRegistry] ${label} failed:`, res.reason);
      }
    });

    const total = totalRes.status === "fulfilled" ? totalRes.value : null;
    const activeCount = activeRes.status === "fulfilled" ? activeRes.value : null;
    const inactiveCount = inactiveRes.status === "fulfilled" ? inactiveRes.value : null;
    const expiringList = expiringRes.status === "fulfilled" ? expiringRes.value : null;

    const totalCount = typeof total === "number" ? total : total?.count ?? total?.total ?? null;
    let expiringCount = null;
    if (expiringList != null) {
      try {
        const rawList = Array.isArray(expiringList) ?
        expiringList :
        Array.isArray(expiringList?.content) ?
        expiringList.content :
        Array.isArray(expiringList?.data) ?
        expiringList.data :
        [];
        expiringCount = rawList.filter((t) => {
          const isActive = t?.isActive ?? t?.active;
          return isActive && t.subscriptionEndDate >= today;
        }).length;
      } catch (err) {
        console.error("[TenantRegistry] Couldn't parse expiring list, raw value was:", expiringList, err);
        expiringCount = null;
      }
    }

    setTenantCounts({
      all: totalCount,
      active: parseCount(activeCount),
      expired: parseCount(inactiveCount),
      expiring: expiringCount
    });
  };

  useEffect(() => {
    loadTenantCounts();
  }, []);


  // Load the service catalog once, to populate the "filter by service" select.
  useEffect(() => {
    let cancelled = false;
    getServiceCatalog()
    .then((data) => {
      if (!cancelled) setServiceOptions(data);
    })
    .catch(() => {
      if (!cancelled) setServiceOptions([]);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadTenants = async (cancelledRef) => {
    setLoading(true);
    setError(null);
    try {
      let list;

      if (serviceFilter !== "all") {
        // GET /tenant-services/service/{serviceId} — tenants that have this
        // service enabled. Status-tab filtering (if any) is then applied
        // client-side on top of this result.
        const tenantsForService = await getTenantsByService(serviceFilter);
        list = tenantsForService;
        if (statusFilter === "active") {
          list = list.filter((t) => t.isActive ?? t.active);
        } else if (statusFilter === "expired") {
          list = list.filter((t) => !(t.isActive ?? t.active));
        } else if (statusFilter === "expiring") {
          const today = new Date().toISOString().slice(0, 10);
          const threshold = new Date();
          threshold.setDate(threshold.getDate() + 30);
          const thresholdDate = threshold.toISOString().slice(0, 10);
          list = list.filter(
            (t) =>
            (t.isActive ?? t.active) &&
            t.subscriptionEndDate >= today &&
            t.subscriptionEndDate <= thresholdDate
          );
        }
      } else if (statusFilter === "expiring") {
        const raw = await getExpiringTenants(30);
        const rawList = Array.isArray(raw) ? raw : raw?.content ?? [];
        const today = new Date().toISOString().slice(0, 10);
        list = rawList.filter((t) => {
          const isActive = t?.isActive ?? t?.active;
          return isActive && t.subscriptionEndDate >= today;
        });
      } else if (statusFilter === "active") {
        const page = await getTenantsByActiveStatus(true);
        list = page?.content ?? [];
      } else if (statusFilter === "expired") {
        const page = await getTenantsByActiveStatus(false);
        list = page?.content ?? [];
      } else {
        const page = await getTenants();
        list = page?.content ?? [];
      }
      if (!cancelledRef.value) {
        const enriched = await Promise.all(
          (list || []).map(async (t) => {
            try {
              const grants = await getTenantServiceGrants(t.tenantId ?? t.id);
              return { ...t, serviceAccess: grants };
            } catch {
              // Non-critical — row just shows "Access not configured" if this fails.
              return t;
            }
          })
        );
        if (!cancelledRef.value) setTenants(enriched);
      }
    } catch (err) {
      if (!cancelledRef.value) setError("Couldn't load tenants: " + err.message);
    } finally {
      if (!cancelledRef.value) setLoading(false);
    }
  };

  useEffect(() => {
    const cancelledRef = { value: false };
    loadTenants(cancelledRef);
    return () => {
      cancelledRef.value = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, serviceFilter]);

  useEffect(() => {
    const query = search.trim();
    if (!query) return undefined;

    const cancelledRef = { value: false };
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await searchTenantsByName(query);
        const rawList = page?.content ?? [];
        if (!cancelledRef.value) {
          const enriched = await Promise.all(
            rawList.map(async (t) => {
              try {
                const grants = await getTenantServiceGrants(t.tenantId ?? t.id);
                return { ...t, serviceAccess: grants };
              } catch {
                return t;
              }
            })
          );
          if (!cancelledRef.value) setTenants(enriched);
        }
      } catch (err) {
        if (!cancelledRef.value) setError("Couldn't search tenants: " + err.message);
      } finally {
        if (!cancelledRef.value) setLoading(false);
      }
    }, 350);

    return () => {
      cancelledRef.value = true;
      clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const filtered = useMemo(() => tenants, [tenants]);

  const handleToggleStatus = async (tenant) => {
    const tenantKey = tenant.tenantId ?? tenant.id;
    const nextIsActive = !tenant.isActive;
    try {
      await toggleTenantStatus(tenant.tenantId);

      // Update the row in place instead of re-fetching the whole list —
      // no full-page reload / spinner, no re-running getTenantServiceGrants
      // for every tenant just to flip one flag.
      setTenants((prev) => {
        const updated = prev.map((t) =>
        (t.tenantId ?? t.id) === tenantKey ? { ...t, isActive: nextIsActive } : t
        );
        // If the current tab only shows one status (Active / Expired) and
        // this tenant no longer belongs there, drop it from view.
        if (statusFilter === "active" && !nextIsActive) {
          return updated.filter((t) => (t.tenantId ?? t.id) !== tenantKey);
        }
        if (statusFilter === "expired" && nextIsActive) {
          return updated.filter((t) => (t.tenantId ?? t.id) !== tenantKey);
        }
        return updated;
      });

      setToast({
        message: `Tenant "${tenant.tenantName}" ${nextIsActive ? "activated" : "deactivated"}.`,
        type: "success"
      });

      // Counts (All/Active/Expired/Expiring tabs) are a handful of cheap
      // count calls with no setLoading — fine to refresh in the background.
      loadTenantCounts();
    } catch (err) {
      setToast({ message: "Couldn't update tenant status: " + err.message, type: "error" });
    }
  };

  return (
    <>
      <LeaveActionToast toast={toast} />
      <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-[var(--color-text-border)]/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-1 rounded-xl border border-[var(--color-text-border)] p-1 bg-white flex-wrap">
            {STATUS_TABS.map((tab) => {
              const isActive = statusFilter === tab.key;
              const count = tenantCounts[tab.key];
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setStatusFilter(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold transition whitespace-nowrap ${
                  isActive ?
                  "bg-[var(--color-accent)] text-white" :
                  "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"}`
                  }>
                  
                  {tab.label}
                  {count !== null && count !== undefined &&
                  <span
                    className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
                    isActive ?
                    "bg-white/25 text-white" :
                    "bg-[var(--color-workspace)]/70 text-[var(--color-text-secondary)]"}`
                    }>
                    
                      {count}
                    </span>
                  }
                </button>);

            })}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="h-9 rounded-xl border border-[var(--color-text-border)] px-3 text-sm text-foreground outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/10 transition bg-white">
              
              <option value="all"><Trans i18nKey="formActions.filterByServiceAll" /></option>
              {serviceOptions.map((svc) =>
              <option key={svc.serviceId} value={svc.serviceId}>
                  {svc.serviceName}
                </option>
              )}
            </select>
            <div className="relative w-full sm:w-72 shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-secondary)] pointer-events-none" />
              <input
                className="w-full h-9 rounded-xl border border-[var(--color-text-border)] pl-9 pr-3 text-sm text-foreground outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/10 transition"
                type="text"
                placeholder={i18n.t("formActions.searchTenants")}
                value={search}
                onChange={(e) => setSearch(e.target.value)} />
              
            </div>
          </div>
        </div>

        {error &&
        <div className="mx-4 sm:mx-6 mt-4 p-3 rounded-xl border border-[var(--color-error)]/25 bg-[var(--color-error)]/10 text-sm font-semibold text-[var(--color-error)]">
            {error}
          </div>
        }

        {loading ?
        <div className="px-6 py-8">
            <div className="flex flex-col gap-3">
              {[1, 2, 3, 4].map((n) =>
            <div key={n} className="h-11 rounded-lg bg-[var(--color-workspace)]/50 animate-pulse" />
            )}
            </div>
          </div> :

        <TenantsTable
          tenants={filtered}
          onEdit={onEditTenant}
          onManageAccess={onManageAccess}
          onToggleStatus={handleToggleStatus} />

        }
      </div>
    </>);

}

function countLeafServices(tree) {
  let count = 0;
  for (const app of tree) {
    for (const mod of app.modules ?? []) {
      for (const sub of mod.sub_modules ?? []) {
        count += (sub.permissions ?? []).length;
      }
    }
  }
  return count;
}

function ServicesModulesCatalogTab() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    getServiceCatalogTree()
    .then((data) => {
      if (!cancelled) setTree(data);
    })
    .catch((err) => {
      if (!cancelled) setLoadError(err?.message || "Failed to load catalog.");
    })
    .finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const serviceCount = useMemo(() => countLeafServices(tree), [tree]);

  return (
    <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <p className="text-[13px] text-[var(--color-text-secondary)] max-w-[520px] leading-relaxed">
          <Trans i18nKey="tenantManagement.fullReadOnlyReferenceOfEveryServiceAvailable" />
        </p>
        {!loading && !loadError &&
        <span className="text-xs font-semibold text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/60 rounded-full px-3 py-1 whitespace-nowrap">
            {serviceCount} <Trans i18nKey="tenantManagement.service" />{serviceCount !== 1 ? "s" : ""}
          </span>
        }
      </div>

      <div className="max-h-[560px] overflow-y-auto pr-1">
        {loading && <p className="text-sm text-[var(--color-text-secondary)] text-center py-6"><Trans i18nKey="tenantManagement.loadingCatalog" /></p>}
        {!loading && loadError &&
        <p className="text-sm text-[var(--color-error)] bg-[var(--color-error)]/10 border border-[var(--color-error)]/25 rounded-xl px-3.5 py-2.5">
            {loadError}
          </p>
        }
        {!loading && !loadError && tree.length === 0 &&
        <p className="text-sm text-[var(--color-text-secondary)] py-3"><Trans i18nKey="tenantManagement.noServicesAvailable" /></p>
        }
        {!loading && !loadError && tree.length > 0 &&
        <AccessTree data={tree} mode="display" defaultExpanded={false} />
        }
      </div>
    </div>);

}

export function TenantRegistryPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("registry");

  return (
    <AppLayout>
      <div className="mb-5">
        <PageBackLink to="/" label="Back" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={ui.pageTitle}>
              {activeTab === "registry" ? "Tenant Registry" : "Services & Modules Catalog"}
            </h1>
          </div>
          {activeTab === "registry" &&
          <Button
            onClick={() => navigate("/psm/config/tenants/create")}
            className="h-11 px-5 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] self-start sm:self-auto">
            
              <Plus className="h-4 w-4" />
              <Trans i18nKey="formActions.createTenant" />
            </Button>
          }
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-[var(--color-text-border)]/70 mb-5">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-1 pb-3 mr-7 text-[15px] font-semibold border-b-2 -mb-px transition ${
              isActive ?
              "text-[var(--color-accent)] border-[var(--color-accent)]" :
              "text-[var(--color-text-secondary)] border-transparent hover:text-foreground"}`
              }>
              
              {tab.label}
            </button>);

        })}
      </div>

      {activeTab === "registry" &&
      <TenantRegistryTab
        onEditTenant={(tenantId) => navigate(`/psm/config/tenants/edit/${tenantId}`)}
        onManageAccess={(tenant) => navigate(`/psm/config/tenants/access/${tenant.tenantId}`)} />

      }
      {activeTab === "catalog" && <ServicesModulesCatalogTab />}
    </AppLayout>);

}
